/* eslint-disable @typescript-eslint/no-unsafe-argument */
import React, {
    createContext,
    useCallback,
    useEffect,
    useMemo,
    useState,
} from "react";

import { graphql, useStaticQuery } from "gatsby";
import { useLocation } from "@gatsbyjs/reach-router";
import { useIsSSR } from "react-aria";

import { gql } from "@apollo/client";

import useVacancyProximityStore, {
    vacanciesWithinDistanceKmQuery,
} from "../components/pages/Vacancies/ProximityFilter/store";
import { useGraphqlLaravelLighthouse } from "./GraphqlLaravelLighthouse";

export const DataContext = createContext();

export const SORT_LOCATION = "SORT_LOCATION";
export const SORT_LOCATION_DESC = "SORT_LOCATION_DESC";

const dynamicQuery = gql`
    query {
        vacancyAudiences(orderBy: { column: NAME, order: ASC }) {
            id
            name
        }
        vacancyCategories(orderBy: { column: NAME, order: ASC }) {
            id
            name
        }
        vacancyCompanies {
            name: company_name
        }
        vacancies(
            orderBy: [
                { column: GEO_CITY, order: ASC }
                { column: EXTERNAL_POSTING_NAME, order: ASC }
            ]
        ) {
            company_name
            apply_online_link
            company_id
            external_posting_name
            geo_city
            geo_coordinates
            geo_country
            geo_name
            geo_state
            geo_street
            geo_zip
            id
            internal_posting_name
            internal_reference_id
            job_ad_url
            job_ad_text
            job_owner_avatarurl
            job_owner_city
            job_owner_email
            job_owner_firstname
            job_owner_function
            job_owner_lastname
            job_owner_phone
            job_owner_salutation
            job_owner_street
            job_owner_title
            job_owner_zip
            job_start_date
            keywords
            locale
            posting_last_updated_date
            project_number
            audiences {
                id
            }
            categories {
                id
            }
        }
    }
`;

const VacancyDataProvider = ({ children }) => {
    const isSSR = useIsSSR();
    const client = useGraphqlLaravelLighthouse();
    const location = useLocation();

    const [proximityData, proximityWasCleared, hasProximityData] =
        useVacancyProximityStore((state) => [
            state.proximityData,
            state.proximityWasCleared,
            state.hasProximityData,
        ]);
    const [searchKeyword, setSearchKeyword] = useState("");
    const [categoryFilter, setCategoryFilter] = useState([]);
    const [audienceFilter, setAudienceFilter] = useState([]);
    const [companyFilter, setCompanyFilter] = useState([]);
    const [cityFilter, setCityFilter] = useState([]);
    const [cities, setCities] = useState([]);
    const [freshData, setFreshData] = useState(null);
    const [shouldSortData, setShouldSortData] = useState(true);
    const [sortKey, setSortKey] = useState(SORT_LOCATION);
    const [isActiveModal, setIsActiveModal] = useState(null);
    const [queryParams] = useState(new URLSearchParams(location.search));
    const [count, setCount] = useState(
        queryParams.get("count") ? parseInt(queryParams.get("count")) : 4
    );
    const [loading, setLoading] = useState(!isSSR);

    const {
        laravelLighthouse: {
            vacancyCategories,
            vacancyAudiences,
            vacancyCompanies,
            vacancies,
        },
    } = useStaticQuery(graphql`
        query {
            laravelLighthouse {
                vacancyAudiences(orderBy: { column: NAME, order: ASC }) {
                    id
                    name
                }
                vacancyCategories(orderBy: { column: NAME, order: ASC }) {
                    id
                    name
                }
                vacancyCompanies {
                    name: company_name
                }
                vacancies(
                    orderBy: [
                        { column: GEO_CITY, order: ASC }
                        { column: EXTERNAL_POSTING_NAME, order: ASC }
                    ]
                ) {
                    company_name
                    apply_online_link
                    company_id
                    external_posting_name
                    geo_city
                    geo_coordinates
                    geo_country
                    geo_name
                    geo_state
                    geo_street
                    geo_zip
                    id
                    internal_posting_name
                    internal_reference_id
                    job_ad_url
                    job_ad_text
                    job_owner_avatarurl
                    job_owner_city
                    job_owner_email
                    job_owner_firstname
                    job_owner_function
                    job_owner_lastname
                    job_owner_phone
                    job_owner_salutation
                    job_owner_street
                    job_owner_title
                    job_owner_zip
                    job_start_date
                    keywords
                    locale
                    posting_last_updated_date
                    project_number
                    audiences {
                        id
                    }
                    categories {
                        id
                    }
                }
            }
        }
    `);

    const companies = useMemo(
        () => vacancyCompanies.map(({ name }, i) => ({ id: String(i), name })),
        [vacancyCompanies]
    );

    useEffect(() => {
        if (freshData?.vacancies.length > 0) {
            setCities(
                Array.from(
                    new Set(
                        freshData.vacancies
                            .filter((vacancy) => !!vacancy.geo_city)
                            .map((vacancy) => vacancy.geo_city)
                    )
                ).sort()
            );
        }
    }, [freshData?.vacancies]);

    useEffect(() => {
        if (proximityWasCleared === true) {
            void loadFreshVacancies();
        }
    }, [proximityWasCleared]);

    useEffect(() => {
        const paramCompanies = queryParams.get("companies");
        if (companies.length > 0 && paramCompanies) {
            //Numeric ids from URL params need to be parsed as integer.
            const parsed = paramCompanies.split(",");
            setCompanyFilter(parsed);
        }
    }, [companies, queryParams]);

    useEffect(() => {
        const paramCategories = queryParams.get("categories");
        if (vacancyCategories.length > 0 && paramCategories) {
            setCategoryFilter(paramCategories.split(","));
        }
    }, [vacancyCategories, queryParams]);

    useEffect(() => {
        const paramAudiences = queryParams.get("audiences");
        if (vacancyAudiences.length > 0 && paramAudiences) {
            setAudienceFilter(paramAudiences.split(","));
        }
    }, [vacancyAudiences, queryParams]);

    useEffect(() => {
        const paramCites = queryParams.get("cities");
        if (cities.length > 0 && paramCites) {
            setCityFilter(paramCites.split(","));
        }
    }, [cities, queryParams]);

    useEffect(() => {
        const paramCount = queryParams.get("count");
        if (paramCount !== null) {
            setCount(parseInt(paramCount));
        }
    }, [setCount, queryParams]);

    useEffect(() => {
        const paramKeyword = queryParams.get("keyword");
        if (paramKeyword) {
            setSearchKeyword(paramKeyword);
        }
    }, []);

    const loadFreshVacancies = useCallback(async () => {
        setLoading(true);

        if (client) {
            const data = await client.query({ query: dynamicQuery });
            if (data.data) {
                const freshVacancies = [...data.data.vacancies];
                setFreshData({
                    ...data.data,
                    ...{ vacancies: freshVacancies },
                });
            }
            setLoading(false);
        }
    }, [client]);

    useEffect(() => {
        if (!isSSR && client) {
            loadFreshVacancies();
        }
    }, [isSSR, client]);

    const preparedVacancies = useMemo(() => {
        if (!vacancies && !freshData) {
            return null;
        }

        let v = (freshData?.vacancies || vacancies).slice();
        if (companyFilter.length > 0) {
            //First we need to map the company names from the filter IDs into a one dimensional array because the vacancies array uses different ids.
            const companyNames = (freshData?.companies || companies)
                .filter((company) => companyFilter.includes(company.id))
                .map((company) => company.name);
            //Then filter the list by matching the names
            v = v.filter((vacancy) =>
                companyNames.includes(vacancy.company_name)
            );
        }

        if (categoryFilter.length > 0) {
            v = v.filter(({ categories }) =>
                categories.some(({ id }) => categoryFilter.includes(id))
            );
        }

        if (audienceFilter.length > 0) {
            v = v.filter(({ audiences }) =>
                audiences.some(({ id }) => audienceFilter.includes(id))
            );
        }

        if (cityFilter.length > 0) {
            v = v.filter((vacancie) => cityFilter.includes(vacancie.geo_city));
        }

        // Previously all the sort logic was here.
        // I think it is better to apply the order by directly in the graphQL query and just reverse the array
        // if the user clicks on the two-way sort button.
        // It will automatically work on either GEO_CITY (default) or DISTANCE (when using the proximity filter)
        if (shouldSortData) {
            if (sortKey === SORT_LOCATION_DESC) {
                v.reverse();
            }
        }
        if (searchKeyword) {
            v = v.filter((item) => {
                return (
                    item.keywords.toLowerCase().includes(searchKeyword) ||
                    item.geo_city.toLowerCase().includes(searchKeyword) ||
                    item.external_posting_name
                        .toLowerCase()
                        .includes(searchKeyword)
                );
            });
        }
        //Set sort true after the data is initially sorted after the proximity search, so the filter works again.
        setShouldSortData(true);

        return v;
    }, [
        vacancies,
        categoryFilter,
        audienceFilter,
        companyFilter,
        sortKey,
        companies,
        freshData,
        searchKeyword,
    ]);

    useEffect(() => {
        if (!isSSR && hasProximityData() && client) {
            client
                .query({
                    query: vacanciesWithinDistanceKmQuery,
                    variables: proximityData,
                })
                .then((data) => {
                    if (data.data.vacancies) {
                        // Data should not be sorted if the user queries data by proximity because backend result is
                        // already sorted by distance to the user.
                        setShouldSortData(false);
                        const freshVacancies = [...data.data.vacancies];
                        setFreshData({
                            ...data.data,
                            ...{ vacancies: freshVacancies },
                        });
                    }
                });
        }
    }, [proximityData]);

    const slicedVacancies = useMemo(() => {
        if (!preparedVacancies) {
            return null;
        }

        if (preparedVacancies < 6) {
            return preparedVacancies;
        }

        return preparedVacancies.slice(0, count);
    }, [preparedVacancies, count]);

    const loadMore = () => {
        const url = new URL(window.location);
        const tmpCount = count + 4;
        url.searchParams.set("count", tmpCount);
        window.history.pushState(null, "", url.toString());
        setCount(tmpCount);
    };

    const handleSort = () => {
        setSortKey(
            sortKey === SORT_LOCATION ? SORT_LOCATION_DESC : SORT_LOCATION
        );
    };

    const data = {
        vacancies,
        preparedVacancies,
        slicedVacancies,
        vacancyCategories,
        vacancyAudiences,
        loading,
        categoryFilter,
        audienceFilter,
        companyFilter,
        sortKey,
        companies,
        isActiveModal,
        freshData,
        searchKeyword,
        cities,
        cityFilter,
    };

    const actions = {
        setCategoryFilter,
        setAudienceFilter,
        setCompanyFilter,
        setSortKey,
        setCount,
        setIsActiveModal,
        loadMore,
        handleSort,
        loadFreshVacancies,
        setSearchKeyword,
        setCityFilter,
    };

    return (
        <DataContext.Provider
            value={{
                data,
                actions,
            }}
        >
            {children}
        </DataContext.Provider>
    );
};

export default VacancyDataProvider;
